import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Eye, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { useLocalize } from '@/hooks/useLandingContent';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Markdown, stripMarkdown } from '@/components/forum/Markdown';
import { ReadingProgress } from '@/components/blog/ReadingProgress';
import { TableOfContents } from '@/components/blog/TableOfContents';
import { PostActions } from '@/components/blog/PostActions';
import { Comments } from '@/components/blog/Comments';

export default function BlogDetail() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const localize = useLocalize();
  const articleRef = useRef<HTMLElement>(null);

  const { data: post, isLoading } = useQuery({
    queryKey: ['blog-detail', slug],
    queryFn: async () => {
      const bySlug = await supabase.from('blog_posts').select('*').eq('slug', slug).eq('is_published', true).maybeSingle();
      if (bySlug.data) return bySlug.data;
      const byId = await supabase.from('blog_posts').select('*').eq('id', slug).eq('is_published', true).maybeSingle();
      return byId.data;
    },
  });

  // Author profile (optional)
  const { data: author } = useQuery({
    queryKey: ['blog-author', post?.author_id],
    enabled: !!post?.author_id,
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('user_id', post!.author_id!)
        .maybeSingle();
      return data;
    },
  });

  // View counter — once per session per post
  useEffect(() => {
    if (!post?.id) return;
    const k = `ere_blog_viewed:${post.id}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, '1');
    supabase.rpc('record_blog_view', {
      _post_id: post.id,
      _referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      _owner_token: null,
    }).then(() => {});
  }, [post?.id]);

  const { data: related } = useQuery({
    queryKey: ['blog-related', post?.id, post?.category, post?.tags?.join(',')],
    enabled: !!post,
    queryFn: async () => {
      const limit = 3;
      const exclude = post!.id;
      // 1. Same category
      let pool: any[] = [];
      if (post!.category) {
        const r = await supabase.from('blog_posts')
          .select('id, slug, title, cover_url, published_at, category, tags')
          .eq('is_published', true).eq('category', post!.category).neq('id', exclude)
          .order('published_at', { ascending: false }).limit(limit);
        pool = r.data || [];
      }
      // 2. Tag overlap
      if (pool.length < limit && post!.tags?.length) {
        const r = await supabase.from('blog_posts')
          .select('id, slug, title, cover_url, published_at, category, tags')
          .eq('is_published', true).neq('id', exclude)
          .overlaps('tags', post!.tags).limit(limit);
        for (const it of r.data || []) {
          if (pool.length >= limit) break;
          if (!pool.find((p) => p.id === it.id)) pool.push(it);
        }
      }
      // 3. Recent
      if (pool.length < limit) {
        const r = await supabase.from('blog_posts')
          .select('id, slug, title, cover_url, published_at, category, tags')
          .eq('is_published', true).neq('id', exclude)
          .order('published_at', { ascending: false }).limit(limit);
        for (const it of r.data || []) {
          if (pool.length >= limit) break;
          if (!pool.find((p) => p.id === it.id)) pool.push(it);
        }
      }
      return pool.slice(0, limit);
    },
  });

  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const bodyText = useMemo(() => {
    if (!post) return '';
    return localize(post.body) || '';
  }, [post, localize]);

  const readMinutes = useMemo(() => {
    const stripped = stripMarkdown(bodyText);
    const words = stripped.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [bodyText]);

  if (isLoading) {
    return <Layout><div className="container py-20 text-center text-muted-foreground">Loading…</div></Layout>;
  }
  if (!post) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-display text-2xl font-bold mb-2">Post not found</h1>
          <Button variant="outline" onClick={() => navigate('/blogs')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to blogs
          </Button>
        </div>
      </Layout>
    );
  }

  const title = localize(post.title);
  const excerpt = localize(post.excerpt);
  const authorName = author?.full_name || post.author_name || 'Editorial Team';
  const authorAvatar = author?.avatar_url || post.author_avatar_url;
  const tags: string[] = post.tags || [];

  return (
    <Layout>
      <SEOHead
        title={`${title} — Ethio Real Estates`}
        description={excerpt || title}
        canonical={`/blogs/${post.slug || post.id}`}
        ogImage={post.og_image_url || post.cover_url || undefined}
      />
      <ReadingProgress />

      <div className="container max-w-7xl py-8 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
        <aside>
          <TableOfContents markdown={bodyText} containerRef={articleRef} />
        </aside>

        <article ref={articleRef} className="max-w-3xl w-full mx-auto">
          <Link to="/blogs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to all blogs
          </Link>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {post.category && (
              <Link to={`/blogs?category=${encodeURIComponent(post.category)}`}>
                <Badge variant="secondary" className="uppercase tracking-wide">{post.category}</Badge>
              </Link>
            )}
            {tags.map((t) => (
              <Link key={t} to={`/blogs?tag=${encodeURIComponent(t)}`}>
                <Badge variant="outline" className="font-normal">#{t}</Badge>
              </Link>
            ))}
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">{title}</h1>

          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              {authorAvatar ? (
                <img src={authorAvatar} alt={authorName} className="h-5 w-5 rounded-full object-cover" />
              ) : (
                <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold inline-flex items-center justify-center">
                  {authorName.charAt(0).toUpperCase()}
                </span>
              )}
              By {authorName}
            </span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(post.published_at)}</span>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {readMinutes} min read</span>
            {typeof post.view_count === 'number' && (
              <>
                <span className="text-border">·</span>
                <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {post.view_count}</span>
              </>
            )}
          </div>

          <div className="mb-6"><PostActions postId={post.id} title={title} /></div>

          {post.cover_url && (
            <img
              src={optimizedImageUrl(post.cover_url, { width: 1200, height: 700, quality: 80 })}
              alt={title}
              className="w-full aspect-[16/9] object-cover rounded-xl mb-8"
            />
          )}

          {excerpt && <p className="text-lg text-muted-foreground leading-relaxed mb-6">{excerpt}</p>}

          {bodyText ? (
            <Markdown className="prose-base">{bodyText}</Markdown>
          ) : (
            <p className="text-muted-foreground italic">Full article coming soon.</p>
          )}

          {tags.length > 0 && (
            <div className="mt-10 pt-6 border-t flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-muted-foreground mr-1">Tagged:</span>
              {tags.map((t) => (
                <Link key={t} to={`/blogs?tag=${encodeURIComponent(t)}`}>
                  <Badge variant="outline" className="font-normal hover:border-primary">#{t}</Badge>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 rounded-xl border bg-card p-4 flex items-center gap-4">
            {authorAvatar ? (
              <img src={authorAvatar} alt={authorName} className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-primary/10 text-primary font-bold inline-flex items-center justify-center">
                {authorName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">Written by</div>
              <div className="font-semibold">{authorName}</div>
            </div>
          </div>

          <Comments postId={post.id} postSlug={post.slug || post.id} />
        </article>
      </div>

      {related && related.length > 0 && (
        <section className="border-t mt-12 py-10 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="font-display text-xl font-bold mb-5">More from the blog</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((r: any) => (
                <Link
                  key={r.id}
                  to={`/blogs/${r.slug || r.id}`}
                  className="block rounded-xl bg-card border overflow-hidden hover:shadow-md transition group"
                >
                  {r.cover_url && (
                    <img
                      src={optimizedImageUrl(r.cover_url, { width: 400, height: 250, quality: 75 })}
                      alt=""
                      className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="p-3">
                    <div className="text-[11px] text-muted-foreground mb-1">{fmtDate(r.published_at)}</div>
                    <div className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary">
                      {localize(r.title)}
                    </div>
                    <span className="text-xs text-primary font-semibold mt-2 inline-flex items-center gap-1">
                      Read more <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
